<!-- Hero Banner Section -->
<section
  class="relative overflow-hidden bg-slate-900 text-white py-12 lg:py-16 border-b border-slate-800"
  aria-labelledby="tutors-directory-heading"
>
  <!-- Subtle Background Gradient Overlay -->
  <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-600/30 via-purple-600/20 to-transparent pointer-events-none"></div>

  <div class="container mx-auto px-6 relative z-10">
    <!-- Breadcrumb -->
    <div class="mb-4">
      <div class="daisy-breadcrumbs text-xs sm:text-sm text-slate-400">
        <ul>
          <li>
            <a routerLink="/" class="hover:text-blue-400 flex items-center gap-1.5">
              <i class="fa-solid fa-house text-xs"></i> Home
            </a>
          </li>
          <li class="text-slate-200 font-semibold">
            <i class="fa-solid fa-chalkboard-user text-xs text-blue-400 mr-1.5"></i>
            Tutors Directory
          </li>
        </ul>
      </div>
    </div>

    <!-- Title & Stats Split Layout -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
      <div class="lg:col-span-8 space-y-4">
        <div
          class="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-400/30 text-blue-300 px-4 py-1.5 rounded-full text-xs font-medium"
        >
          <i class="fa-solid fa-user-check text-blue-400"></i>
          <span>Verified Educator Directory</span>
        </div>
        <h1
          id="tutors-heading"
          class="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight"
        >
          Find Expert Sri Lankan Tutors
        </h1>
        <p class="text-slate-300 text-base sm:text-lg max-w-2xl leading-relaxed">
          Connect with verified tutors for physical home visits, group tuition, institute lectures, and live Zoom classes across Sri Lanka.
        </p>
      </div>

      <!-- Quick Platform Counter Badges -->
      <div class="lg:col-span-4 flex flex-wrap lg:justify-end gap-3">
        <div class="bg-slate-800/80 border border-slate-700/80 p-4 rounded-2xl flex-1 sm:flex-none text-center shadow-lg min-w-[120px]">
          <p class="text-2xl font-extrabold text-blue-400">{{ totalDocs }}+</p>
          <p class="text-xs text-slate-400 flex items-center justify-center gap-1.5 mt-0.5">
            <i class="fa-solid fa-user-graduate text-blue-400"></i> Active Tutors
          </p>
        </div>
        <div class="bg-slate-800/80 border border-slate-700/80 p-4 rounded-2xl flex-1 sm:flex-none text-center shadow-lg min-w-[120px]">
          <p class="text-2xl font-extrabold text-purple-400">25</p>
          <p class="text-xs text-slate-400 flex items-center justify-center gap-1.5 mt-0.5">
            <i class="fa-solid fa-location-dot text-purple-400"></i> Districts
          </p>
        </div>
      </div>
    </div>

  </div>
</section>

<!-- Main Directory Content Section (Light Theme) -->
<section class="py-12 bg-slate-50 min-h-screen text-slate-900 relative">
  <div class="container mx-auto px-6">
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">
      
      <!-- Filters Sidebar (Light Theme) -->
      <div class="lg:col-span-3">
        <div
          class="daisy-card bg-white border border-slate-200/80 p-6 rounded-2xl shadow-sm sticky top-24 space-y-6"
        >
          <div class="flex justify-between items-center pb-4 border-b border-slate-200/80">
            <h3 class="text-lg font-bold text-slate-900 flex items-center gap-2 m-0">
              <i class="fa-solid fa-sliders text-blue-600"></i> Filters
            </h3>
            <button
              (click)="clearFilters()"
              class="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors bg-none border-none cursor-pointer"
            >
              Clear All
            </button>
          </div>

          <div class="space-y-4">
            <!-- Search Keyword -->
            <div class="space-y-1.5">
              <label class="text-xs font-bold uppercase tracking-wider text-slate-500 block">
                Search Tutor
              </label>
              <div class="relative">
                <i class="fa-solid fa-magnifying-glass absolute left-3.5 top-3.5 text-slate-400 text-sm"></i>
                <input
                  type="text"
                  [value]="filters.search"
                  (input)="onSearch($event)"
                  placeholder="Name or bio keywords..."
                  class="daisy-input daisy-input-bordered w-full pl-10 pr-4 py-2 bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 text-sm rounded-xl focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>

            <!-- Subject -->
            <div class="space-y-1.5">
              <label class="text-xs font-bold uppercase tracking-wider text-slate-500 block">
                Subject
              </label>
              <select
                [(ngModel)]="filters.subject"
                (change)="onFilterChange()"
                class="daisy-select daisy-select-bordered w-full bg-slate-50 border-slate-200 text-slate-800 text-sm rounded-xl focus:border-blue-500 focus:outline-none"
              >
                <option value="">All Subjects</option>
                <option *ngFor="let sub of subjectsList" [value]="sub">{{ sub }}</option>
              </select>
            </div>

            <!-- Medium -->
            <div class="space-y-1.5">
              <label class="text-xs font-bold uppercase tracking-wider text-slate-500 block">
                Medium
              </label>
              <select
                [(ngModel)]="filters.medium"
                (change)="onFilterChange()"
                class="daisy-select daisy-select-bordered w-full bg-slate-50 border-slate-200 text-slate-800 text-sm rounded-xl focus:border-blue-500 focus:outline-none"
              >
                <option value="">All Mediums</option>
                <option *ngFor="let med of mediumsList" [value]="med">{{ med }} Medium</option>
              </select>
            </div>

            <!-- Class Format -->
            <div class="space-y-1.5">
              <label class="text-xs font-bold uppercase tracking-wider text-slate-500 block">
                Class Format
              </label>
              <select
                [(ngModel)]="filters.classFormat"
                (change)="onFilterChange()"
                class="daisy-select daisy-select-bordered w-full bg-slate-50 border-slate-200 text-slate-800 text-sm rounded-xl focus:border-blue-500 focus:outline-none"
              >
                <option value="">All Formats</option>
                <option value="Online">Online Class</option>
                <option value="HomeVisit">Individual (Home Visit)</option>
                <option value="Group">Group Class</option>
                <option value="Institute">Institute Class</option>
              </select>
            </div>

            <!-- District -->
            <div class="space-y-1.5">
              <label class="text-xs font-bold uppercase tracking-wider text-slate-500 block">
                District
              </label>
              <select
                [(ngModel)]="filters.district"
                (change)="onFilterChange()"
                class="daisy-select daisy-select-bordered w-full bg-slate-50 border-slate-200 text-slate-800 text-sm rounded-xl focus:border-blue-500 focus:outline-none"
              >
                <option value="">All Districts</option>
                <option *ngFor="let dist of districtsList" [value]="dist">{{ dist }}</option>
              </select>
            </div>

          </div>
        </div>
      </div>

      <!-- Results Grid (Light Theme) -->
      <div class="lg:col-span-9 space-y-6">
        
        <!-- Results Info Bar -->
        <div class="flex flex-wrap items-center justify-between gap-4 bg-white border border-slate-200/80 p-4 rounded-2xl shadow-sm">
          <p class="text-sm font-medium text-slate-600 m-0 flex items-center gap-2">
            <i class="fa-solid fa-user-check text-blue-600"></i>
            Found <span class="text-slate-900 font-bold">{{ totalDocs }}</span> tutor profiles
          </p>
          <div *ngIf="loading" class="flex items-center gap-2 text-blue-600 text-xs font-semibold">
            <span class="daisy-loading daisy-loading-spinner daisy-loading-sm text-blue-600"></span>
            Loading profiles...
          </div>
        </div>

        <!-- Empty State -->
        <div
          *ngIf="tutors.length === 0 && !loading"
          class="daisy-card bg-white border border-slate-200/80 p-12 text-center rounded-2xl space-y-4 shadow-sm"
        >
          <div class="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto border border-slate-200">
            <i class="fa-solid fa-user-slash text-3xl text-slate-400"></i>
          </div>
          <h3 class="text-lg font-bold text-slate-900 m-0">No Tutors Found</h3>
          <p class="text-slate-500 text-sm max-w-md mx-auto m-0">
            We couldn't find any tutors matching your search parameters. Try adjusting your filters or search keywords.
          </p>
          <div>
            <button
              (click)="clearFilters()"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors border-none"
            >
              Reset Filters
            </button>
          </div>
        </div>

        <!-- Tutor Grid Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          <div
            *ngFor="let tutor of tutors"
            class="daisy-card bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow flex flex-col h-full overflow-hidden"
          >
            <!-- Card Header Cover -->
            <div class="h-20 bg-gradient-to-r from-blue-50 to-indigo-50 relative flex-shrink-0">
              <!-- Verified Badge -->
              <span
                *ngIf="tutor.profile?.tutorInfo?.isVerifiedTutor"
                class="absolute right-4 top-4 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm"
              >
                <i class="fa-solid fa-circle-check text-xs"></i> Verified
              </span>
            </div>

            <!-- Card Body Content -->
            <div class="px-6 pb-6 flex flex-col flex-grow relative -mt-10">
              <!-- Avatar -->
              <div class="relative w-20 h-20 rounded-full border-4 border-white bg-white overflow-hidden shadow-sm mb-3">
                <img
                  [src]="getAvatarUrl(tutor.profile?.personal?.avatar)"
                  [alt]="tutor.profile?.personal?.firstName"
                  class="w-full h-full object-cover"
                />
              </div>

              <!-- Name & Headline -->
              <div class="mb-3">
                <h4 class="text-base font-bold text-slate-800 m-0">
                  {{ tutor.profile?.personal?.firstName }} {{ tutor.profile?.personal?.lastName }}
                </h4>
                <p class="text-xs text-slate-500 font-semibold m-0 mt-0.5 truncate">
                  {{ tutor.profile?.professional?.jobTitle || 'Expert Tutor' }}
                </p>
              </div>

              <!-- Subjects Tags -->
              <div class="flex flex-wrap gap-1.5 mb-4">
                <span
                  *ngFor="let sub of (tutor.profile?.tutorInfo?.subjects || [] | slice:0:3)"
                  class="bg-indigo-50 text-indigo-700 text-[10px] font-bold px-2 py-0.5 rounded"
                >
                  {{ sub }}
                </span>
                <span
                  *ngIf="(tutor.profile?.tutorInfo?.subjects || []).length > 3"
                  class="bg-slate-100 text-slate-600 text-[10px] font-bold px-2 py-0.5 rounded"
                >
                  +{{ (tutor.profile?.tutorInfo?.subjects || []).length - 3 }}
                </span>
              </div>

              <!-- Rate Info & View Button -->
              <div class="mt-auto border-t border-slate-100 pt-4 flex justify-between items-center">
                <div>
                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wider m-0">Starting Rate</p>
                  <p class="text-sm font-extrabold text-slate-800 m-0">
                    {{ tutor.profile?.tutorInfo?.ratePerHour ? 'LKR ' + (tutor.profile?.tutorInfo?.ratePerHour | number) + '/hr' : 'Rates Variable' }}
                  </p>
                </div>
                <a
                  [routerLink]="['/tutors', tutor.slug || tutor._id]"
                  class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-colors no-underline"
                >
                  View Profile
                </a>
              </div>

            </div>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div *ngIf="pages > 1" class="flex justify-center mt-10">
          <div class="flex gap-1.5 items-center">
            <button
              [disabled]="page === 1"
              (click)="onPageChange(page - 1)"
              class="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-500 cursor-pointer"
            >
              <i class="fa-solid fa-chevron-left text-xs"></i>
            </button>

            <button
              *ngFor="let p of getPagesArray()"
              (click)="onPageChange(p)"
              [class.bg-blue-600]="p === page"
              [class.text-white]="p === page"
              [class.border-blue-600]="p === page"
              [class.border-slate-200]="p !== page"
              [class.text-slate-600]="p !== page"
              [class.hover:bg-blue-50]="p !== page"
              [class.hover:text-blue-600]="p !== page"
              class="w-9 h-9 flex items-center justify-center rounded-lg border text-sm font-bold transition-all cursor-pointer"
            >
              {{ p }}
            </button>

            <button
              [disabled]="page === pages"
              (click)="onPageChange(page + 1)"
              class="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-500 cursor-pointer"
            >
              <i class="fa-solid fa-chevron-right text-xs"></i>
            </button>
          </div>
        </div>

      </div>

    </div>
  </div>
</section>
